import { AlertCircle } from "@untitledui/icons";
import { cva } from "class-variance-authority";
import type { PropsWithChildren } from "react";
import { cx } from "@/utils/cx";
import { Badge } from "../badge";

const fieldControlVariants = cva("flex items-center gap-2 text-secondary", {
	variants: {
		size: {
			sm: "text-sm",
			md: "text-md",
			lg: "text-lg",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type FormFieldControlProps = PropsWithChildren & {
	label?: string;
	htmlFor: string;
	error?: string;
	hint?: string | null;
	className?: string;
	isRequired?: boolean;
	isRequiredAsBadge?: boolean;
	showIconError?: boolean;
	disabled?: boolean;
	size?: "sm" | "md" | "lg";
};

export const FormFieldControl = ({
	label = "",
	htmlFor,
	error = "",
	hint = "",
	className = "",
	isRequired = false,
	isRequiredAsBadge = false,
	showIconError = true,
	disabled = false,
	children,
	size = "md",
}: FormFieldControlProps) => {
	const showBadge = isRequired && isRequiredAsBadge;
	const showAsterisk = isRequired && !isRequiredAsBadge;

	return (
		<div className={cx("flex w-full min-w-0 flex-col gap-1.5", className)}>
			{!!label && (
				<label
					aria-disabled={disabled}
					className={fieldControlVariants({ size })}
					htmlFor={htmlFor}
				>
					{label}
					{!!showBadge && (
						<Badge variant={error ? "destructive" : "default"}>
							Obrigatório
						</Badge>
					)}
					{!!showAsterisk && (
						<span
							aria-hidden="true"
							className={cx(
								"text-brand-tertiary",
								!!error && "text-error-primary"
							)}
						>
							*
						</span>
					)}
				</label>
			)}
			{!!hint && (
				<p className="wrap-break-word text-sm text-tertiary">{hint}</p>
			)}
			<div className="flex w-full min-w-0 flex-col gap-1.5">
				<div className="relative flex w-full min-w-0">
					{children}
					{!!error && showIconError && (
						<AlertCircle
							className="-translate-y-1/2 absolute top-1/2 right-3 text-error-secondary"
							size={16}
						/>
					)}
				</div>
				{!!error && !isRequiredAsBadge && (
					<span className="text-error-primary text-sm">{error}</span>
				)}
			</div>
		</div>
	);
};
